import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from 'react-query';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import './animations.css';
import {
  ArrowLeftIcon,
  DocumentTextIcon,
  CalendarIcon,
  MapPinIcon,
  ClockIcon,
  EyeIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  XMarkIcon,
  UserIcon,
  ChartBarIcon,
  FunnelIcon as FilterIcon,
  ArrowPathIcon as RefreshIcon,
  ChevronDownIcon,
  AdjustmentsVerticalIcon as AdjustmentsIcon,
  XMarkIcon as XIcon,
  ArrowsUpDownIcon as SortAscendingIcon,
  DocumentTextIcon as DocumentReportIcon,
  PaperAirplaneIcon,
  PencilIcon,
  ExclamationTriangleIcon as ExclamationIcon,
  DocumentMagnifyingGlassIcon as DocumentSearchIcon,
  InformationCircleIcon,
  IdentificationIcon,
  MapPinIcon as LocationMarkerIcon,
  BuildingOfficeIcon as OfficeBuildingIcon,
  FlagIcon,
  ChartPieIcon,
  QuestionMarkCircleIcon,
  ExclamationCircleIcon,
  ClipboardDocumentListIcon as ClipboardListIcon,
  ClipboardDocumentCheckIcon as ClipboardCheckIcon,
  ArrowUturnLeftIcon as ReplyIcon,
  CheckIcon,
  HashtagIcon,
  PencilSquareIcon as PencilAltIcon,
  PhotoIcon as PhotographIcon,
  XCircleIcon,
  TagIcon,
  ChatBubbleLeftEllipsisIcon as AnnotationIcon,
  ChatBubbleLeftIcon as ChatAltIcon,
  CodeBracketIcon as CodeIcon,
  DocumentIcon,
  ChevronDownIcon as ChevronDownIconSolid,
  PaperClipIcon,
  ArrowDownTrayIcon as DownloadIcon,
  CheckBadgeIcon as BadgeCheckIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from './LoadingSpinner';

interface MyReportsListProps {
  onClose: () => void;
}

const MyReportsList: React.FC<MyReportsListProps> = ({ onClose }) => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [timelineFilter, setTimelineFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');
  const [selectedReport, setSelectedReport] = useState<any>(null);
  const [animateItems, setAnimateItems] = useState(false);

  // References for animations
  const reportItemsRef = useRef<(HTMLDivElement | null)[]>([]);

  // Initialize animation when component loads
  useEffect(() => {
    // Trigger animation after a short delay
    setTimeout(() => {
      setAnimateItems(true);
    }, 100);
  }, []);

  // Fetch user-specific reports
  const { data: reports, isLoading, error } = useQuery(
    ['my-reports', user?.id],
    () => api.reports.getUserReports(user?.id?.toString() || ''),
    {
      refetchInterval: 30000,
      enabled: !!user?.id, // Only fetch if user ID is available
    }
  );

  const reportsList = Array.isArray(reports?.data?.data?.reports)
    ? reports!.data.data.reports
    : Array.isArray(reports?.data?.reports)
      ? reports!.data.reports
      : Array.isArray(reports?.data)
        ? reports!.data
        : [];

  // Filter and sort reports based on search and filter criteria
  const filteredReports = reportsList
    .filter((report: any) => {
      // Search across multiple fields
      const matchesSearch = !searchTerm ||
        report.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        report.observer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        report.id?.toString().includes(searchTerm) ||
        report.incident_type?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesTimeline = timelineFilter === 'all' || report.timeline === timelineFilter;
      const matchesStatus = statusFilter === 'all' || report.status === statusFilter;

      return matchesSearch && matchesTimeline && matchesStatus;
    })
    .sort((a: any, b: any) => {
      const dateA = new Date(a.created_at || a.submitted_at || 0).getTime();
      const dateB = new Date(b.created_at || b.submitted_at || 0).getTime();

      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });

  const getTimelineBadge = (timeline: string) => {
    const timelineConfig = {
      morning: { class: 'bg-blue-100 text-blue-800', label: 'Morning (6am-12pm)' },
      afternoon: { class: 'bg-yellow-100 text-yellow-800', label: 'Afternoon (12pm-4pm)' },
      vote_count: { class: 'bg-green-100 text-green-800', label: 'Vote Count (4pm-6am)' },
    };
    const config = timelineConfig[timeline as keyof typeof timelineConfig] || timelineConfig.morning;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.class}`}>
        {config.label}
      </span>
    );
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'submitted': return 'bg-blue-100 text-blue-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'under_review': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleViewReport = (report: any) => {
    setSelectedReport(report);
  };

  const handleCloseModal = () => {
    setSelectedReport(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <DocumentTextIcon className="w-12 h-12 mx-auto mb-4 text-red-500" />
          <h3 className="mb-2 text-lg font-medium text-gray-900">Error Loading Reports</h3>
          <p className="text-gray-600">Unable to load your E-Day reports. Please try again.</p>
          <button onClick={onClose} className="mt-4 btn btn-primary">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 animate-fadeIn">
      <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Header */}
        <div className="p-6 mb-8 bg-white border border-gray-100 rounded-lg shadow-sm animate-slideInTop" style={{ animationDelay: '50ms' }}>
          <div className="flex flex-col justify-between md:flex-row md:items-center">
            <div className="flex flex-col space-y-4 md:flex-row md:items-center md:space-y-0 md:space-x-6">
              <button
                onClick={onClose}
                className="flex items-center space-x-2 btn btn-secondary w-fit"
              >
                <ArrowLeftIcon className="w-5 h-5" />
                <span>Back to Dashboard</span>
              </button>
              <div>
                <h1 className="flex items-center text-2xl font-bold text-gray-900">
                  <DocumentTextIcon className="mr-3 text-blue-600 h-7 w-7" />
                  My E-Day Submissions
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                  View and manage your election day observation reports
                </p>
              </div>
            </div>
            <div className="flex items-center px-3 py-2 mt-4 text-sm text-gray-500 rounded-md md:mt-0 bg-blue-50">
              <UserIcon className="w-4 h-4 mr-2 text-blue-600" />
              <span>Observer ID: <span className="font-medium text-blue-700">{user?.id}</span></span>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="p-6 mb-6 bg-white border border-gray-100 rounded-lg shadow-sm animate-slideInTop" style={{ animationDelay: '150ms' }}>
          <div className="flex flex-col items-center justify-between mb-4 md:flex-row">
            <div className="flex items-center mb-4 md:mb-0">
              <FilterIcon className="w-5 h-5 mr-2 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-900">Search & Filter</h2>
            </div>
            <div className="flex items-center bg-blue-50 px-3 py-1.5 rounded-full text-sm">
              <DocumentTextIcon className="w-4 h-4 mr-2 text-blue-600" />
              <span className="font-medium text-blue-800">{filteredReports.length}</span>
              <span className="mx-1 text-gray-600">of</span>
              <span className="text-gray-700">{reportsList.length} reports</span>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            {/* Search */}
            <div className="relative transition-all duration-200 rounded-md hover:shadow-md">
              <MagnifyingGlassIcon className="absolute w-5 h-5 text-gray-400 left-3 top-3" />
              <input
                type="text"
                placeholder="Search reports..."
                className="w-full rounded-md border border-gray-300 py-2.5 px-4 pl-10 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute text-gray-400 right-3 top-3 hover:text-gray-600"
                >
                  <XIcon className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Timeline Filter */}
            <div className="relative transition-all duration-200 rounded-md hover:shadow-md">
              <select
                className="w-full appearance-none rounded-md border border-gray-300 py-2.5 px-4 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={timelineFilter}
                onChange={(e) => setTimelineFilter(e.target.value)}
              >
                <option value="all">All Timelines</option>
                <option value="morning">Morning</option>
                <option value="afternoon">Afternoon</option>
                <option value="vote_count">Vote Count</option>
              </select>
              <ClockIcon className="absolute w-4 h-4 text-gray-500 pointer-events-none right-3 top-3" />
            </div>

            {/* Status Filter */}
            <div className="relative transition-all duration-200 rounded-md hover:shadow-md">
              <select
                className="w-full appearance-none rounded-md border border-gray-300 py-2.5 px-4 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="submitted">Submitted</option>
                <option value="under_review">Under Review</option>
                <option value="completed">Completed</option>
              </select>
              <BadgeCheckIcon className="absolute w-4 h-4 text-gray-500 pointer-events-none right-3 top-3" />
            </div>

            {/* Sort Option */}
            <div className="relative transition-all duration-200 rounded-md hover:shadow-md">
              <select
                className="w-full appearance-none rounded-md border border-gray-300 py-2.5 px-4 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
              </select>
              <SortAscendingIcon className="absolute w-4 h-4 text-gray-500 pointer-events-none right-3 top-3" />
            </div>
          </div>
        </div>

        {/* Reports List */}
        {filteredReports.length === 0 ? (
          <div className="p-12 text-center bg-white border border-gray-100 rounded-lg shadow-sm animate-scaleIn">
            <div className="flex items-center justify-center w-20 h-20 mx-auto mb-4 rounded-full bg-gray-50">
              <DocumentSearchIcon className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="mb-3 text-xl font-medium text-gray-900">No Reports Found</h3>
            <p className="max-w-md mx-auto text-gray-600">
              {searchTerm || timelineFilter !== 'all' || statusFilter !== 'all'
                ? 'No reports match your current filter criteria. Try adjusting your filters or search terms.'
                : 'You haven\'t submitted any election day reports yet. Reports you submit will appear here.'}
            </p>
            {(searchTerm || timelineFilter !== 'all' || statusFilter !== 'all') && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setTimelineFilter('all');
                  setStatusFilter('all');
                }}
                className="inline-flex items-center px-4 py-2 mt-4 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <RefreshIcon className="w-4 h-4 mr-2" />
                Reset All Filters
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredReports.map((report: any, index: number) => (
              <div
                key={report.id}
                ref={el => reportItemsRef.current[index] = el}
                className={`bg-white rounded-lg shadow-sm border border-gray-100 hover:border-blue-200 hover:shadow-md transition-all duration-200 staggered-item ${animateItems ? 'staggered-item-visible' : ''}`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="p-5">
                  <div className="flex flex-col justify-between md:flex-row md:items-start">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-3 mb-3">
                        <div className="flex items-center">
                          <DocumentReportIcon className={`h-6 w-6 mr-2 ${report.status === 'completed' ? 'text-green-600' : 'text-blue-600'}`} />
                          <h3 className="text-lg font-semibold text-gray-900">
                            Report #{report.id.toString().padStart(4, '0')}
                          </h3>
                        </div>

                        {getTimelineBadge(report.timeline)}

                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(report.status)}`}>
                          {report.status === 'completed' && <CheckCircleIcon className="w-4 h-4 mr-1" />}
                          {report.status === 'under_review' && <ClockIcon className="w-4 h-4 mr-1" />}
                          {report.status === 'submitted' && <PaperAirplaneIcon className="w-4 h-4 mr-1" />}
                          {report.status === 'draft' && <PencilIcon className="w-4 h-4 mr-1" />}
                          {report.status?.replace('_', ' ') || 'Unknown'}
                        </span>
                      </div>

                      <div className="flex flex-wrap mb-4 text-sm text-gray-500 gap-x-6 gap-y-2">
                        <div className="flex items-center">
                          <CalendarIcon className="h-4 w-4 mr-1.5 text-gray-400" />
                          <span>Created: {formatDate(report.created_at)}</span>
                        </div>
                        {report.location && (
                          <div className="flex items-center">
                            <MapPinIcon className="h-4 w-4 mr-1.5 text-gray-400" />
                            <span className="truncate max-w-[180px]">{report.location}</span>
                          </div>
                        )}
                        <div className="flex items-center">
                          <ClockIcon className="h-4 w-4 mr-1.5 text-gray-400" />
                          <span>Updated: {formatDate(report.submitted_at || report.created_at)}</span>
                        </div>
                      </div>

                      {/* Report Summary */}
                      <div className="p-4 mb-3 rounded-lg bg-gray-50">
                        <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
                          <div className="flex flex-col">
                            <span className="mb-1 text-gray-500">Questions</span>
                            <span className="font-semibold text-gray-800">{report.total_questions || '0'}</span>
                          </div>
                          <div className="flex flex-col">
                            <span className="mb-1 text-gray-500">Answered</span>
                            <span className="font-semibold text-gray-800">{report.answered_questions || '0'}</span>
                          </div>
                          <div className="flex flex-col">
                            <span className="mb-1 text-gray-500">Completion</span>
                            <div className="flex items-center">
                              <span className="mr-2 font-semibold text-gray-800">
                                {report.completion_percentage ? `${report.completion_percentage}%` : '0%'}
                              </span>
                              <div className="w-16 h-2 bg-gray-200 rounded-full">
                                <div
                                  className={`h-2 rounded-full ${(report.completion_percentage || 0) >= 100 ? 'bg-green-500' :
                                      (report.completion_percentage || 0) >= 60 ? 'bg-blue-500' :
                                        'bg-yellow-500'
                                    }`}
                                  style={{ width: `${report.completion_percentage || 0}%` }}
                                ></div>
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-col">
                            <span className="mb-1 text-gray-500">Observer</span>
                            <span className="font-semibold text-gray-800">{report.observer_name || user?.name || 'Unknown'}</span>
                          </div>
                        </div>
                      </div>

                      {report.incident_type && (
                        <div className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-red-50 text-red-700 border border-red-100">
                          <ExclamationIcon className="w-3 h-3 mr-1" />
                          Incident: {report.incident_type}
                        </div>
                      )}
                    </div>

                    <div className="mt-4 md:mt-0 md:ml-4">
                      <button
                        onClick={() => handleViewReport(report)}
                        className="flex items-center space-x-2 transition-transform btn btn-primary btn-sm hover:scale-105"
                      >
                        <EyeIcon className="w-4 h-4" />
                        <span>View Details</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Report Detail Modal */}
        {selectedReport && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-60 animate-fadeIn">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto animate-scaleIn">
              {/* Modal Header */}
              <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 bg-white border-b border-gray-200">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center">
                    <DocumentReportIcon className={`h-6 w-6 mr-2 ${selectedReport.status === 'completed' ? 'text-green-600' : 'text-blue-600'}`} />
                    <h3 className="text-xl font-semibold text-gray-900">
                      Report #{selectedReport.id.toString().padStart(4, '0')}
                    </h3>
                  </div>
                  {getTimelineBadge(selectedReport.timeline)}
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedReport.status)}`}>
                    {selectedReport.status === 'completed' && <CheckCircleIcon className="w-4 h-4 mr-1" />}
                    {selectedReport.status === 'under_review' && <ClockIcon className="w-4 h-4 mr-1" />}
                    {selectedReport.status === 'submitted' && <PaperAirplaneIcon className="w-4 h-4 mr-1" />}
                    {selectedReport.status === 'draft' && <PencilIcon className="w-4 h-4 mr-1" />}
                    {selectedReport.status?.replace('_', ' ') || 'Unknown'}
                  </span>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="p-2 transition-colors rounded-full hover:bg-gray-100"
                  aria-label="Close modal"
                >
                  <XMarkIcon className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="px-6 py-6">
                {/* Basic Information */}
                <div className="mb-8">
                  <div className="flex items-center mb-4">
                    <InformationCircleIcon className="w-5 h-5 mr-2 text-blue-600" />
                    <h4 className="text-lg font-semibold text-gray-900">
                      Report Overview
                    </h4>
                  </div>

                  {/* Report Metadata Grid */}
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div className="p-4 transition-shadow bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md">
                      <div className="flex items-center mb-2">
                        <IdentificationIcon className="w-5 h-5 mr-2 text-blue-600" />
                        <span className="text-sm font-medium text-gray-700">Report Details</span>
                      </div>
                      <div className="space-y-2">
                        <div>
                          <span className="text-xs text-gray-500">Report ID:</span>
                          <p className="text-sm font-semibold text-gray-900">{selectedReport.id}</p>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500">Status:</span>
                          <p className="text-sm font-semibold text-gray-900">{selectedReport.status?.replace('_', ' ')}</p>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500">Timeline:</span>
                          <p className="text-sm font-semibold text-gray-900">{selectedReport.timeline?.replace('_', ' ')}</p>
                        </div>
                      </div>
                    </div>

                    <div className="p-4 transition-shadow bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md">
                      <div className="flex items-center mb-2">
                        <UserIcon className="w-5 h-5 mr-2 text-green-600" />
                        <span className="text-sm font-medium text-gray-700">Observer Information</span>
                      </div>
                      <div className="space-y-2">
                        <div>
                          <span className="text-xs text-gray-500">Observer Name:</span>
                          <p className="text-sm font-semibold text-gray-900">{selectedReport.observer_name || user?.name || 'Unknown'}</p>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500">Observer ID:</span>
                          <p className="text-sm font-semibold text-gray-900">{selectedReport.user_id || selectedReport.observer_id || user?.id || 'N/A'}</p>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500">Phone:</span>
                          <p className="text-sm font-semibold text-gray-900">{selectedReport.phone || user?.phone || 'N/A'}</p>
                        </div>
                      </div>
                    </div>

                    <div className="p-4 transition-shadow bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md">
                      <div className="flex items-center mb-2">
                        <ClockIcon className="w-5 h-5 mr-2 text-purple-600" />
                        <span className="text-sm font-medium text-gray-700">Time Information</span>
                      </div>
                      <div className="space-y-2">
                        <div>
                          <span className="text-xs text-gray-500">Created:</span>
                          <p className="text-sm font-semibold text-gray-900">{formatDate(selectedReport.created_at)}</p>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500">Submitted:</span>
                          <p className="text-sm font-semibold text-gray-900">{formatDate(selectedReport.submitted_at || selectedReport.created_at)}</p>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500">Last Updated:</span>
                          <p className="text-sm font-semibold text-gray-900">{formatDate(selectedReport.updated_at || selectedReport.created_at)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Location Information */}
                {selectedReport.location && (
                  <div className="mb-8">
                    <div className="flex items-center mb-4">
                      <MapPinIcon className="w-5 h-5 mr-2 text-blue-600" />
                      <h5 className="text-lg font-semibold text-gray-900">Location</h5>
                    </div>
                    <div className="p-4 border border-blue-100 rounded-lg bg-blue-50">
                      <div className="flex items-center">
                        <LocationMarkerIcon className="w-5 h-5 mr-2 text-blue-600" />
                        <span className="text-sm font-medium text-gray-900">{selectedReport.location}</span>
                      </div>
                      {selectedReport.constituency && (
                        <div className="flex items-center mt-2">
                          <OfficeBuildingIcon className="w-5 h-5 mr-2 text-blue-600" />
                          <span className="text-sm text-gray-700">Constituency: <span className="font-medium">{selectedReport.constituency}</span></span>
                        </div>
                      )}
                      {selectedReport.polling_station && (
                        <div className="flex items-center mt-2">
                          <FlagIcon className="w-5 h-5 mr-2 text-blue-600" />
                          <span className="text-sm text-gray-700">Polling Station: <span className="font-medium">{selectedReport.polling_station}</span></span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Report Statistics */}
                <div className="mb-8">
                  <div className="flex items-center mb-4">
                    <ChartPieIcon className="w-5 h-5 mr-2 text-blue-600" />
                    <h5 className="text-lg font-semibold text-gray-900">Report Statistics</h5>
                  </div>
                  <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                    <div className="p-4 text-center transition-all duration-300 transform border border-blue-200 rounded-lg shadow-sm bg-gradient-to-br from-blue-50 to-blue-100 hover:shadow-md hover:-translate-y-1">
                      <div className="flex items-center justify-center mb-2">
                        <QuestionMarkCircleIcon className="text-blue-600 h-7 w-7" />
                      </div>
                      <div className="text-3xl font-bold text-blue-700">
                        {selectedReport.total_questions ||
                          (selectedReport.responses ? selectedReport.responses.length : '0')}
                      </div>
                      <div className="text-sm font-medium text-blue-600">Total Questions</div>
                    </div>

                    <div className="p-4 text-center transition-all duration-300 transform border border-green-200 rounded-lg shadow-sm bg-gradient-to-br from-green-50 to-green-100 hover:shadow-md hover:-translate-y-1">
                      <div className="flex items-center justify-center mb-2">
                        <CheckCircleIcon className="text-green-600 h-7 w-7" />
                      </div>
                      <div className="text-3xl font-bold text-green-700">
                        {selectedReport.answered_questions ||
                          (selectedReport.responses ?
                            selectedReport.responses.filter((r: any) => r.response_value && r.response_value !== '').length :
                            '0')}
                      </div>
                      <div className="text-sm font-medium text-green-600">Responses Given</div>
                    </div>

                    <div className="p-4 text-center transition-all duration-300 transform border border-purple-200 rounded-lg shadow-sm bg-gradient-to-br from-purple-50 to-purple-100 hover:shadow-md hover:-translate-y-1">
                      <div className="flex items-center justify-center mb-2">
                        <DocumentTextIcon className="text-purple-600 h-7 w-7" />
                      </div>
                      <div className="flex flex-col items-center text-3xl font-bold text-purple-700">
                        <span>
                          {selectedReport.completion_percentage ?
                            `${selectedReport.completion_percentage}%` :
                            selectedReport.responses && selectedReport.total_questions ?
                              `${Math.round((selectedReport.responses.length / selectedReport.total_questions) * 100)}%` :
                              '0%'}
                        </span>
                        <div className="w-16 h-2 mt-1 bg-gray-200 rounded-full">
                          <div
                            className="h-2 bg-purple-500 rounded-full"
                            style={{
                              width: `${selectedReport.completion_percentage ||
                                (selectedReport.responses && selectedReport.total_questions ?
                                  Math.round((selectedReport.responses.length / selectedReport.total_questions) * 100) :
                                  0)}%`
                            }}
                          ></div>
                        </div>
                      </div>
                      <div className="text-sm font-medium text-purple-600">Completion</div>
                    </div>

                    {selectedReport.incident_type ? (
                      <div className="p-4 text-center transition-all duration-300 transform border border-red-200 rounded-lg shadow-sm bg-gradient-to-br from-red-50 to-red-100 hover:shadow-md hover:-translate-y-1">
                        <div className="flex items-center justify-center mb-2">
                          <ExclamationCircleIcon className="text-red-600 h-7 w-7" />
                        </div>
                        <div className="text-lg font-bold text-red-700 truncate">
                          {selectedReport.incident_type}
                        </div>
                        <div className="text-sm font-medium text-red-600">Incident Type</div>
                      </div>
                    ) : (
                      <div className="p-4 text-center transition-all duration-300 transform border border-gray-200 rounded-lg shadow-sm bg-gradient-to-br from-gray-50 to-gray-100 hover:shadow-md hover:-translate-y-1">
                        <div className="flex items-center justify-center mb-2">
                          <ClipboardListIcon className="text-gray-600 h-7 w-7" />
                        </div>
                        <div className="text-3xl font-bold text-gray-700">
                          {selectedReport.timeline === 'morning' ? 'AM' :
                            selectedReport.timeline === 'afternoon' ? 'PM' :
                              selectedReport.timeline === 'vote_count' ? 'VC' : '??'}
                        </div>
                        <div className="text-sm font-medium text-gray-600">Report Session</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Questions and Responses */}
                {selectedReport.responses && selectedReport.responses.length > 0 && (
                  <div className="mb-8">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <ClipboardCheckIcon className="w-5 h-5 mr-2 text-blue-600" />
                        <h5 className="text-lg font-semibold text-gray-900">Questions & Responses</h5>
                      </div>
                      <div className="px-3 py-1 text-sm text-gray-500 rounded-full bg-blue-50">
                        {selectedReport.responses.length} {selectedReport.responses.length === 1 ? 'response' : 'responses'}
                      </div>
                    </div>

                    <div className="space-y-4">
                      {selectedReport.responses.map((response: any, index: number) => (
                        <div
                          key={response.id || index}
                          className="p-4 transition-all duration-200 bg-white border border-gray-200 rounded-lg shadow-sm hover:border-blue-200 hover:shadow-md"
                        >
                          <div>
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <div className="flex items-center justify-center w-6 h-6 text-xs font-bold text-blue-800 bg-blue-100 rounded-full">
                                    {index + 1}
                                  </div>
                                  <p className="text-sm font-medium text-gray-900">
                                    {response.question_text}
                                  </p>
                                </div>
                                <p className="mb-3 ml-8 text-xs text-gray-500">
                                  <span className="inline-flex items-center mr-3">
                                    <IdentificationIcon className="w-3 h-3 mr-1 text-gray-400" />
                                    ID: {response.question_id}
                                  </span>
                                  <span className="inline-flex items-center">
                                    <TagIcon className="w-3 h-3 mr-1 text-gray-400" />
                                    Category: {response.category || 'General'}
                                  </span>
                                </p>
                              </div>
                              <div className="flex-shrink-0 ml-4">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${response.question_type === 'yesno' ? 'bg-blue-100 text-blue-800' :
                                    response.question_type === 'time' ? 'bg-green-100 text-green-800' :
                                      response.question_type === 'number' ? 'bg-purple-100 text-purple-800' :
                                        response.question_type === 'text' ? 'bg-gray-100 text-gray-800' :
                                          response.question_type === 'photo' ? 'bg-pink-100 text-pink-800' :
                                            response.question_type === 'location' ? 'bg-yellow-100 text-yellow-800' :
                                              'bg-orange-100 text-orange-800'
                                  }`}>
                                  {response.question_type === 'yesno' && <CheckIcon className="w-3 h-3 mr-1" />}
                                  {response.question_type === 'time' && <ClockIcon className="w-3 h-3 mr-1" />}
                                  {response.question_type === 'number' && <HashtagIcon className="w-3 h-3 mr-1" />}
                                  {response.question_type === 'text' && <PencilAltIcon className="w-3 h-3 mr-1" />}
                                  {response.question_type === 'photo' && <PhotographIcon className="w-3 h-3 mr-1" />}
                                  {response.question_type === 'location' && <LocationMarkerIcon className="w-3 h-3 mr-1" />}
                                  {response.question_type}
                                </span>
                              </div>
                            </div>

                            <div className="ml-8">
                              {response.response_value ? (
                                <div className="p-3 mt-2 border border-blue-200 rounded-lg bg-gradient-to-r from-blue-50 to-blue-100">
                                  <div className="flex items-start">
                                    <ReplyIcon className="h-4 w-4 text-blue-500 mr-2 mt-0.5" />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-blue-900">
                                        {response.question_type === 'yesno' && response.response_value === 'yes' && (
                                          <span className="inline-flex items-center text-green-700">
                                            <CheckCircleIcon className="w-4 h-4 mr-1" />
                                            Yes
                                          </span>
                                        )}
                                        {response.question_type === 'yesno' && response.response_value === 'no' && (
                                          <span className="inline-flex items-center text-red-700">
                                            <XCircleIcon className="w-4 h-4 mr-1" />
                                            No
                                          </span>
                                        )}
                                        {response.question_type !== 'yesno' && (
                                          <span>{response.response_value}</span>
                                        )}
                                      </p>

                                      {response.response_data && (
                                        <div className="pt-2 mt-2 border-t border-blue-200">
                                          <p className="mb-1 text-xs font-medium text-blue-700">Additional details:</p>
                                          <pre className="p-2 overflow-x-auto text-xs text-blue-600 bg-white border border-blue-200 rounded max-h-40">
                                            {typeof response.response_data === 'string'
                                              ? response.response_data
                                              : JSON.stringify(response.response_data, null, 2)}
                                          </pre>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="p-3 mt-2 text-sm italic text-gray-500 border border-gray-200 rounded-lg bg-gray-50">
                                  No response provided
                                </div>
                              )}

                              <p className="flex items-center mt-2 text-xs text-gray-400">
                                <ClockIcon className="w-3 h-3 mr-1" />
                                Answered: {formatDate(response.created_at)}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Notes or Comments */}
                {selectedReport.notes && (
                  <div className="mb-8">
                    <div className="flex items-center mb-4">
                      <AnnotationIcon className="w-5 h-5 mr-2 text-blue-600" />
                      <h5 className="text-lg font-semibold text-gray-900">Notes</h5>
                    </div>
                    <div className="p-4 border-l-4 rounded-lg bg-gradient-to-r from-yellow-50 to-amber-50 border-amber-400">
                      <div className="flex">
                        <ChatAltIcon className="flex-shrink-0 w-5 h-5 mr-3 text-amber-500" />
                        <p className="text-sm italic text-gray-700">{selectedReport.notes}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Additional Report Data */}
                {selectedReport.report_data && (
                  <div className="mb-8">
                    <div className="flex items-center mb-4">
                      <CodeIcon className="w-5 h-5 mr-2 text-blue-600" />
                      <h5 className="text-lg font-semibold text-gray-900">Additional Data</h5>
                    </div>
                    <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                      <details className="cursor-pointer">
                        <summary className="flex items-center text-sm font-medium text-gray-700 hover:text-gray-900">
                          <DocumentIcon className="w-4 h-4 mr-2 text-gray-500" />
                          <span>View Raw Report Data</span>
                          <ChevronDownIcon className="w-4 h-4 ml-2 text-gray-500" />
                        </summary>
                        <div className="p-3 mt-3 overflow-y-auto bg-gray-100 border border-gray-200 rounded-lg max-h-60">
                          <pre className="text-xs text-gray-600 whitespace-pre-wrap">
                            {typeof selectedReport.report_data === 'string'
                              ? selectedReport.report_data
                              : JSON.stringify(selectedReport.report_data, null, 2)}
                          </pre>
                        </div>
                      </details>
                    </div>
                  </div>
                )}

                {/* Attachments Section - if the API supports it in the future */}
                {selectedReport.attachments && selectedReport.attachments.length > 0 && (
                  <div className="mb-8">
                    <div className="flex items-center mb-4">
                      <PaperClipIcon className="w-5 h-5 mr-2 text-blue-600" />
                      <h5 className="text-lg font-semibold text-gray-900">Attachments</h5>
                    </div>
                    <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                      {selectedReport.attachments.map((attachment: any, index: number) => (
                        <div key={index} className="flex items-center p-3 border border-gray-200 rounded-lg bg-gray-50">
                          <DocumentIcon className="w-8 h-8 mr-3 text-blue-500" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {attachment.filename || `Attachment ${index + 1}`}
                            </p>
                            <p className="text-xs text-gray-500">
                              {attachment.size ? `${Math.round(attachment.size / 1024)} KB` : ''}
                            </p>
                          </div>
                          <button className="p-1 ml-2 text-gray-400 hover:text-gray-600">
                            <DownloadIcon className="w-5 h-5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="sticky bottom-0 flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gradient-to-t from-white via-white to-transparent">
                <div className="flex items-center text-sm text-gray-500">
                  <ClockIcon className="w-4 h-4 mr-1 text-gray-400" />
                  <span>Last updated: {formatDate(selectedReport.updated_at || selectedReport.submitted_at || selectedReport.created_at)}</span>
                </div>
                <div className="flex space-x-3">
                  {selectedReport.status !== 'completed' && (
                    <button
                      onClick={() => {
                        // Future functionality to edit the report
                        alert('Edit functionality will be available in a future update');
                      }}
                      className="flex items-center space-x-1 btn btn-outline-primary btn-sm"
                    >
                      <PencilIcon className="w-4 h-4" />
                      <span>Edit</span>
                    </button>
                  )}
                  <button
                    onClick={handleCloseModal}
                    className="btn btn-primary"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyReportsList;
